import React, { useCallback, useEffect, useState } from "react";
import { X, Clock, CheckCircle2, FileText, Award, AlertCircle, Save } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Teacher-side submissions view — opens when a teacher clicks an assignment
 * row. Shows all submissions (with "missing" students too), lets teacher
 * grade homework (score + feedback) or short-answer quiz questions (per-
 * question partial credit).
 */
export default function SubmissionsDrawer({ assignmentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/staff/assignments/${assignmentId}/submissions`);
      setData(r.data);
      // Auto-select first submission only when drawer opens with no active selection.
      setSelectedId((cur) => cur || r.data.submissions[0]?.submission_id || null);
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  // Reset selection whenever the drawer is re-pointed at a different assignment,
  // then reload. Body scroll-lock is paired with drawer lifecycle.
  useEffect(() => {
    setSelectedId(null);
    load();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [assignmentId, load]);

  const selected = data?.submissions.find((s) => s.submission_id === selectedId) || null;

  return (
    <div className="fixed inset-0 z-[55]" data-testid="submissions-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="absolute top-0 end-0 h-full w-full max-w-5xl bg-bone shadow-2xl flex flex-col border-s border-border">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Submissions</div>
            <div className="font-display text-lg font-semibold truncate">{data?.assignment?.title || "…"}</div>
            {data && (
              <div className="text-xs text-muted-foreground">
                {data.submissions.length} submitted · {data.missing_students.length} missing
              </div>
            )}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-secondary" data-testid="submissions-drawer-close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Left list */}
          <div className="w-72 border-e border-border bg-white overflow-y-auto shrink-0">
            {loading && <div className="px-4 py-6 text-xs text-muted-foreground">Loading…</div>}
            {data?.submissions.length === 0 && !loading && (
              <div className="px-4 py-6 text-xs text-muted-foreground">No submissions yet.</div>
            )}
            {data?.submissions.map((s) => (
              <button
                key={s.submission_id}
                onClick={() => setSelectedId(s.submission_id)}
                data-testid={`submission-select-${s.submission_id}`}
                className={`w-full text-start px-4 py-3 border-b border-border flex items-center gap-3 hover:bg-bone transition ${
                  selectedId === s.submission_id ? "bg-sage/5" : ""
                }`}
              >
                {s.student?.picture ? (
                  <img src={s.student.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-sage/10 text-sage text-xs font-semibold flex items-center justify-center">
                    {(s.student?.name || s.student?.email || "?")[0].toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.student?.name || s.student?.email}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    {s.status === "reviewed" ? (
                      <><CheckCircle2 className="w-3 h-3 text-sage" /> {s.percentage != null ? `${s.percentage}%` : "Reviewed"}</>
                    ) : (
                      <><Clock className="w-3 h-3" /> Submitted</>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {data?.missing_students.length > 0 && (
              <div className="px-4 py-3 mt-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Not submitted ({data.missing_students.length})
              </div>
            )}
            {data?.missing_students.map((u) => (
              <div key={u.user_id} className="px-4 py-2 flex items-center gap-2 opacity-60">
                <div className="w-6 h-6 rounded-full bg-secondary text-[10px] font-semibold flex items-center justify-center">
                  {(u.name || u.email || "?")[0].toUpperCase()}
                </div>
                <div className="text-xs truncate">{u.name || u.email}</div>
              </div>
            ))}
          </div>

          {/* Right detail */}
          <div className="flex-1 overflow-y-auto bg-bone">
            {!selected && (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Select a submission to review.
              </div>
            )}
            {selected && (
              <SubmissionReview
                submission={selected}
                assignment={data.assignment}
                onSaved={() => { load(); }}
              />
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function SubmissionReview({ submission, assignment, onSaved }) {
  const isHomework = submission.assignment_type === "homework";
  const isQuiz = submission.assignment_type === "teacher_quiz";

  const [score, setScore] = useState(submission.score != null ? submission.score : "");
  const [feedback, setFeedback] = useState(submission.feedback || "");
  const [manualScores, setManualScores] = useState(submission.manual_scores || {});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setScore(submission.score != null ? submission.score : "");
    setFeedback(submission.feedback || "");
    setManualScores(submission.manual_scores || {});
    setMsg("");
  }, [submission.submission_id]);

  const handleSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      const body = { feedback, status: "reviewed" };
      if (isHomework) body.score = score === "" ? null : Number(score);
      if (isQuiz) body.manual_scores = Object.fromEntries(Object.entries(manualScores).map(([k, v]) => [k, Number(v) || 0]));
      await api.patch(`/staff/submissions/${submission.submission_id}`, body);
      setMsg("Saved.");
      onSaved();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-6 space-y-5" data-testid="submission-review">
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${
          submission.status === "reviewed" ? "bg-sage/10 text-sage" : "bg-amber-100 text-amber-700"
        }`}>
          {submission.status}
        </div>
        <div className="text-xs text-muted-foreground">
          Submitted {submission.submitted_at?.slice(0, 16).replace("T", " ")}
        </div>
        {submission.percentage != null && (
          <div className="ms-auto inline-flex items-center gap-1.5 text-sm font-display font-semibold text-sage">
            <Award className="w-4 h-4" /> {submission.percentage}%
          </div>
        )}
      </div>

      {isHomework && <HomeworkReview submission={submission} />}
      {isQuiz && (
        <QuizReview
          submission={submission}
          questions={assignment.questions || []}
          manualScores={manualScores}
          setManualScores={setManualScores}
        />
      )}

      <div className="bg-white border border-border rounded-2xl p-4 space-y-3">
        <div className="font-display text-sm font-semibold">Feedback</div>
        {isHomework && (
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Score (0–100)</label>
            <input
              type="number" min={0} max={100} step={1}
              value={score} onChange={(e) => setScore(e.target.value)}
              data-testid="submission-score-input"
              className="mt-1 w-32 px-3 py-1.5 rounded-lg border border-border bg-bone text-sm focus:outline-none focus:border-sage"
            />
          </div>
        )}
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Written feedback for the student…"
          data-testid="submission-feedback-input"
          className="w-full px-3 py-2 rounded-lg border border-border bg-bone text-sm focus:outline-none focus:border-sage min-h-[70px]"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="submission-save-grade"
            className="inline-flex items-center gap-1.5 bg-sage hover:bg-sage-dark disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save & mark reviewed"}
          </button>
          {msg && <span className="text-xs text-muted-foreground">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

function HomeworkReview({ submission }) {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <div className="bg-white border border-border rounded-2xl p-4 space-y-3" data-testid="homework-review">
      <div className="font-display text-sm font-semibold flex items-center gap-2">
        <FileText className="w-4 h-4 text-sage" /> Student's work
      </div>
      {submission.text && (
        <div className="text-sm whitespace-pre-wrap leading-relaxed text-foreground bg-bone border border-border rounded-lg p-3">
          {submission.text}
        </div>
      )}
      {!submission.text && <div className="text-xs text-muted-foreground">(No text)</div>}
      {submission.file_ids?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Attachments</div>
          {submission.file_ids.map((fid) => (
            <a key={fid} href={`${apiBase}/api/files/${fid}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage/10 hover:bg-sage/20 text-sage text-xs font-medium rounded-lg transition">
              <FileText className="w-3.5 h-3.5" /> View attachment
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function QuizReview({ submission, questions, manualScores, setManualScores }) {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const answers = submission.answers || {};

  return (
    <div className="space-y-3" data-testid="quiz-review">
      <div className="bg-white border border-border rounded-2xl p-4 flex items-center gap-4 text-sm">
        <div><span className="text-muted-foreground">Auto-graded:</span> <span className="font-semibold">{submission.auto_score}/{submission.auto_total}</span></div>
        {submission.requires_manual && (
          <div className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold">
            <AlertCircle className="w-3.5 h-3.5" /> Short-answer questions need manual grading
          </div>
        )}
      </div>
      {questions.map((q, i) => {
        const given = answers[q.question_id];
        const isMcq = q.type === "mcq";
        const isTF = q.type === "truefalse";
        const isShort = q.type === "short";
        let correct = null;
        if (isMcq) correct = Number(given) === Number(q.correct_index);
        if (isTF) correct = Boolean(given) === Boolean(q.correct_bool);

        return (
          <div key={q.question_id} className="bg-white border border-border rounded-2xl p-4 space-y-2" data-testid={`quiz-review-q-${i}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Q{i + 1}</span>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                {q.type}
              </span>
              {correct === true && <span className="text-[10px] text-sage font-semibold">✓ Correct</span>}
              {correct === false && <span className="text-[10px] text-terracotta font-semibold">✗ Wrong</span>}
            </div>
            <div className="text-sm">{q.prompt}</div>
            {q.image_file_id && (
              <img src={`${apiBase}/api/files/${q.image_file_id}`} alt="" className="max-h-40 rounded-md border border-border" />
            )}
            {isMcq && (
              <div className="space-y-1">
                {(q.options || []).map((opt, idx) => (
                  <div key={`${q.question_id}-opt-${idx}`} className={`text-xs px-3 py-1.5 rounded-md ${
                    idx === q.correct_index ? "bg-sage/10 text-sage" :
                    idx === Number(given) ? "bg-terracotta/10 text-terracotta" : "bg-bone"
                  }`}>
                    {String.fromCharCode(65 + idx)}. {opt}
                    {idx === q.correct_index && " ✓"}
                    {idx === Number(given) && idx !== q.correct_index && " ← student's answer"}
                  </div>
                ))}
              </div>
            )}
            {isTF && (
              <div className="text-xs text-muted-foreground">
                Student: <strong>{given === true ? "True" : given === false ? "False" : "—"}</strong> · Correct: <strong>{q.correct_bool ? "True" : "False"}</strong>
              </div>
            )}
            {isShort && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  <strong>Reference answer:</strong> {q.correct_text || "—"}
                </div>
                <div className="bg-bone border border-border rounded-lg p-2.5 text-sm whitespace-pre-wrap">
                  {given || <span className="text-muted-foreground">(no answer)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted-foreground">Points (0 or 1):</label>
                  <input
                    type="number" min={0} max={1} step={0.5}
                    value={manualScores[q.question_id] ?? 0}
                    onChange={(e) => setManualScores({ ...manualScores, [q.question_id]: e.target.value })}
                    data-testid={`quiz-manual-${i}`}
                    className="w-20 px-2 py-1 rounded-md border border-border bg-bone text-sm focus:outline-none focus:border-sage"
                  />
                </div>
              </div>
            )}
            {q.explanation && (
              <div className="text-[11px] text-muted-foreground border-t border-border pt-2">
                <strong>Explanation:</strong> {q.explanation}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
